import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { EMPTY_STATE, type Member, type Photo, type Place, type TripState } from '@map/shared';
import {
  ApiError,
  addComment,
  createPlace,
  deleteComment,
  deletePhoto,
  deletePlace,
  deleteVote,
  fetchState,
  openSession,
  patchPlace,
  putVote,
  setItinerary,
  uploadPhoto,
  type PlaceInput,
  type PlacePatch,
} from '../api.js';
import { createRealtime, type Realtime, type RealtimeStatus } from '../realtime.js';
import { applyEvent } from './reducer.js';
import { itineraryPlaces } from './selectors.js';

/** Phase de l'application : quel ecran doit etre monte. */
export type Phase = 'loading' | 'gate' | 'ready';

interface Identity {
  readonly memberId: number;
  readonly pseudo: string;
}

const IDENTITY_KEY = 'map.identity';

/**
 * Le cookie de session est httpOnly : le client ne peut pas le lire, donc au
 * rechargement il sait qu'il est authentifie (le GET /api/state passe) mais
 * pas QUI il est. On memorise donc l'identite renvoyee par POST /api/session
 * dans le localStorage. Sans elle, impossible de savoir quel vote est le sien
 * ni quel commentaire il a le droit de supprimer.
 */
function readIdentity(): Identity | null {
  try {
    const raw = window.localStorage.getItem(IDENTITY_KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const memberId = (parsed as Record<string, unknown>)['memberId'];
    const pseudo = (parsed as Record<string, unknown>)['pseudo'];
    if (typeof memberId !== 'number' || typeof pseudo !== 'string') return null;
    return { memberId, pseudo };
  } catch {
    return null;
  }
}

function writeIdentity(identity: Identity | null): void {
  try {
    if (identity === null) window.localStorage.removeItem(IDENTITY_KEY);
    else window.localStorage.setItem(IDENTITY_KEY, JSON.stringify(identity));
  } catch {
    // Navigation privee ou stockage plein : on continue, l'utilisateur devra
    // resaisir le code au prochain chargement.
  }
}

export interface Store {
  readonly phase: Phase;
  readonly state: TripState;
  readonly me: Member | null;
  readonly connection: RealtimeStatus;
  /** Message transitoire affiche en bandeau (suppression concurrente, panne). */
  readonly banner: string | null;
  readonly setBanner: (message: string | null) => void;

  readonly signIn: (code: string, pseudo: string) => Promise<void>;
  readonly resync: () => Promise<void>;

  readonly addPlace: (input: PlaceInput) => Promise<Place>;
  readonly editPlace: (placeId: number, patch: PlacePatch) => Promise<Place>;
  readonly removePlace: (placeId: number) => Promise<void>;

  readonly setVote: (placeId: number, value: -1 | 1) => Promise<void>;
  readonly clearVote: (placeId: number) => Promise<void>;

  readonly postComment: (placeId: number, body: string) => Promise<void>;
  readonly removeComment: (commentId: number, placeId: number) => Promise<void>;

  readonly sendPhoto: (
    placeId: number,
    file: File,
    onProgress: (ratio: number) => void,
  ) => Promise<Photo>;
  readonly removePhoto: (photoId: number, placeId: number) => Promise<void>;

  readonly reorder: (placeIds: readonly number[]) => Promise<void>;
  readonly promote: (placeId: number) => Promise<void>;
  readonly demote: (placeId: number) => Promise<void>;
}

const StoreCtx = createContext<Store | null>(null);

export function useStore(): Store {
  const store = useContext(StoreCtx);
  if (store === null) throw new Error('useStore doit etre appele sous <StoreProvider>');
  return store;
}

export function StoreProvider({ children }: { children: ReactNode }): ReactNode {
  const [phase, setPhase] = useState<Phase>('loading');
  const [state, setState] = useState<TripState>(EMPTY_STATE);
  const [identity, setIdentity] = useState<Identity | null>(() => readIdentity());
  const [connection, setConnection] = useState<RealtimeStatus>('idle');
  const [banner, setBanner] = useState<string | null>(null);

  // L'etat vit dans une ref en plus du state React : les callbacks du
  // WebSocket sont crees une seule fois et liraient sinon un `state` fige a
  // la valeur du premier rendu.
  const stateRef = useRef<TripState>(EMPTY_STATE);
  const realtimeRef = useRef<Realtime | null>(null);

  const commit = useCallback((next: TripState) => {
    stateRef.current = next;
    setState(next);
  }, []);

  const push = useCallback(
    (event: Parameters<typeof applyEvent>[1]) => {
      commit(applyEvent(stateRef.current, event));
    },
    [commit],
  );

  const resync = useCallback(async () => {
    const fresh = await fetchState();
    commit(fresh);
  }, [commit]);

  /**
   * Toute erreur 401 renvoie a l'ecran de code : le cookie a 90 jours de
   * validite mais le service peut avoir tourne sa cle de signature.
   */
  const guard = useCallback(async <T,>(work: () => Promise<T>): Promise<T> => {
    try {
      return await work();
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        writeIdentity(null);
        setIdentity(null);
        setPhase('gate');
      }
      throw error;
    }
  }, []);

  // --- amorcage ------------------------------------------------------------

  useEffect(() => {
    let cancelled = false;
    const known = readIdentity();
    if (known === null) {
      setPhase('gate');
      return;
    }
    void (async () => {
      try {
        const fresh = await fetchState();
        if (cancelled) return;
        commit(fresh);
        setPhase('ready');
      } catch (error) {
        if (cancelled) return;
        if (error instanceof ApiError && error.status === 401) {
          writeIdentity(null);
          setIdentity(null);
        } else {
          setBanner('Le serveur est injoignable. Verifie ta connexion.');
        }
        setPhase('gate');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [commit]);

  // --- canal temps reel ----------------------------------------------------

  useEffect(() => {
    if (phase !== 'ready') return;
    const realtime = createRealtime({
      onEvent: (event) => {
        commit(applyEvent(stateRef.current, event));
      },
      onReconnect: () => {
        // Le serveur ne rejoue pas les evenements manques : on reprend l'etat
        // entier, seule facon d'etre certain de ne pas diverger.
        void resync().catch(() => {
          setBanner('Resynchronisation impossible. Recharge la page.');
        });
      },
      onStatus: setConnection,
    });
    realtimeRef.current = realtime;
    realtime.start();
    return () => {
      realtime.stop();
      realtimeRef.current = null;
    };
  }, [phase, commit, resync]);

  // --- actions -------------------------------------------------------------

  const signIn = useCallback(
    async (code: string, pseudo: string) => {
      const result = await openSession(code, pseudo);
      const next: Identity = { memberId: result.memberId, pseudo: result.pseudo };
      writeIdentity(next);
      setIdentity(next);
      const fresh = await fetchState();
      commit(fresh);
      setBanner(result.reused ? `Tu reprends l'identite de ${result.pseudo}.` : null);
      setPhase('ready');
    },
    [commit],
  );

  const addPlace = useCallback(
    (input: PlaceInput) =>
      guard(async () => {
        const place = await createPlace(input);
        // On applique la reponse REST tout de suite : l'echo WebSocket
        // arrivera, mais applyEvent est idempotent donc il ne fera rien.
        push({ type: 'place:created', place });
        return place;
      }),
    [guard, push],
  );

  const editPlace = useCallback(
    (placeId: number, patch: PlacePatch) =>
      guard(async () => {
        const place = await patchPlace(placeId, patch);
        push({ type: 'place:updated', place });
        return place;
      }),
    [guard, push],
  );

  const removePlace = useCallback(
    (placeId: number) =>
      guard(async () => {
        await deletePlace(placeId);
        push({ type: 'place:deleted', placeId });
      }),
    [guard, push],
  );

  const setVote = useCallback(
    (placeId: number, value: -1 | 1) =>
      guard(async () => {
        const vote = await putVote(placeId, value);
        push({ type: 'vote:changed', vote });
      }),
    [guard, push],
  );

  const clearVote = useCallback(
    (placeId: number) =>
      guard(async () => {
        await deleteVote(placeId);
        const memberId = identity?.memberId ?? null;
        if (memberId !== null) push({ type: 'vote:removed', placeId, memberId });
      }),
    [guard, push, identity],
  );

  const postComment = useCallback(
    (placeId: number, body: string) =>
      guard(async () => {
        const comment = await addComment(placeId, body);
        push({ type: 'comment:added', comment });
      }),
    [guard, push],
  );

  const removeComment = useCallback(
    (commentId: number, placeId: number) =>
      guard(async () => {
        await deleteComment(commentId);
        push({ type: 'comment:deleted', commentId, placeId });
      }),
    [guard, push],
  );

  const sendPhoto = useCallback(
    (placeId: number, file: File, onProgress: (ratio: number) => void) =>
      guard(async () => {
        const photo = await uploadPhoto(placeId, file, onProgress);
        push({ type: 'photo:added', photo });
        return photo;
      }),
    [guard, push],
  );

  const removePhoto = useCallback(
    (photoId: number, placeId: number) =>
      guard(async () => {
        await deletePhoto(photoId);
        push({ type: 'photo:deleted', photoId, placeId });
      }),
    [guard, push],
  );

  const reorder = useCallback(
    (placeIds: readonly number[]) =>
      guard(async () => {
        const result = await setItinerary(placeIds);
        push({ type: 'itinerary:reordered', places: result.places });
      }),
    [guard, push],
  );

  const promote = useCallback(
    (placeId: number) => {
      const current = itineraryPlaces(stateRef.current).map((place) => place.id);
      if (current.includes(placeId)) return Promise.resolve();
      return reorder([...current, placeId]);
    },
    [reorder],
  );

  const demote = useCallback(
    (placeId: number) => {
      const current = itineraryPlaces(stateRef.current)
        .map((place) => place.id)
        .filter((id) => id !== placeId);
      return reorder(current);
    },
    [reorder],
  );

  const me = useMemo<Member | null>(() => {
    if (identity === null) return null;
    return state.members.find((member) => member.id === identity.memberId) ?? null;
  }, [identity, state.members]);

  const value = useMemo<Store>(
    () => ({
      phase,
      state,
      me,
      connection,
      banner,
      setBanner,
      signIn,
      resync,
      addPlace,
      editPlace,
      removePlace,
      setVote,
      clearVote,
      postComment,
      removeComment,
      sendPhoto,
      removePhoto,
      reorder,
      promote,
      demote,
    }),
    [
      phase,
      state,
      me,
      connection,
      banner,
      signIn,
      resync,
      addPlace,
      editPlace,
      removePlace,
      setVote,
      clearVote,
      postComment,
      removeComment,
      sendPhoto,
      removePhoto,
      reorder,
      promote,
      demote,
    ],
  );

  return <StoreCtx.Provider value={value}>{children}</StoreCtx.Provider>;
}
